import type { AnalyticsField, AnalyticsFilter, AnalyticsMetric, TimeBucket } from '../analytics/AnalyticsTypes';

export type { AnalyticsField, AnalyticsFilter, AnalyticsMetric, TimeBucket } from '../analytics/AnalyticsTypes';

/**
 * Wire contract for the surface AI assist, shared by the bmai-web BFF (which
 * produces an {@link AssistPlan} by routing a request to a workflow) and the
 * frontend engine (which consumes it). Type-only imports of the analytics
 * vocabulary keep this module runtime-self-contained, so both the tsc-built BFF
 * and the Vite-built frontend can import it.
 */

/** A chart kind the assist can render; every kind is drill-in capable. */
export type AssistVizKind = 'ranked-bars' | 'donut' | 'trend';

/** Where the user is when they ask — lets the assist scope and phrase its answer. */
export interface AssistContext {
    surfaceId: string;
    surfaceTitle: string;
    topicId?: string;
    /** Model-facing description of the surface's analytical thesis — steers the assist toward this page's focus and fields. */
    surfaceScope?: string;
    /** The current drill-in view's title + question, when the user is on a topic page. */
    topicFocus?: string;
    /** The page's resolved global date window (UTC, `YYYY-MM-DD HH:MM:SS`, end exclusive) — assist queries must scope to it. */
    dateWindow?: { start: string; end: string };
    activeFilters: AnalyticsFilter[];
}

/** The browser → BFF request: a natural-language ask plus page context. */
export interface AssistRequest {
    prompt: string;
    context: AssistContext;
}

/**
 * Render an interactive chart (or answer a question with one). The frontend
 * builds the actual aggregate/trend query from these semantic choices and runs
 * it through the existing analytics gateway — the model never writes SQL.
 */
export interface AssistChartPlan {
    workflow: 'chart';
    title: string;
    narrative: string;
    viz: AssistVizKind;
    primaryField: AnalyticsField;
    metric: AnalyticsMetric;
    bucket?: TimeBucket;
    filters?: AnalyticsFilter[];
}

/** Propose ambient filter pills that re-scope the whole page. */
export interface AssistFilterPlan {
    workflow: 'filter';
    narrative: string;
    filters: AnalyticsFilter[];
}

/** The ask needs a signal the classifier doesn't capture yet — offer to create a classification. */
export interface AssistProposeFieldPlan {
    workflow: 'propose-field';
    narrative: string;
    missingConcept: string;
    fieldDraftPrompt: string;
    /** The classification category the proposed field best belongs to. */
    category: AssistFieldCategory;
}

/** Nothing actionable — answer plainly without a chart, filter, or field offer. */
export interface AssistUnsupportedPlan {
    workflow: 'unsupported';
    narrative: string;
}

export type AssistWorkflow = AssistPlan['workflow'];

export type AssistPlan = AssistChartPlan | AssistFilterPlan | AssistProposeFieldPlan | AssistUnsupportedPlan;

/** A queryable field the assist exposes, with model-facing guidance for routing. */
export interface AssistFieldDef {
    value: AnalyticsField;
    label: string;
    description: string;
}

/** A metric the assist exposes, with model-facing guidance. */
export interface AssistMetricDef {
    value: AnalyticsMetric;
    label: string;
    description: string;
}

/**
 * The analytics vocabulary the assist supports. This is the single source of
 * truth for the enums the BFF puts in its workflow schemas and the labels the
 * UI shows — a deliberately curated subset of the full analytics field set.
 */
export const ASSIST_FIELDS: AssistFieldDef[] = [
    { value: 'user_department', label: 'Department', description: 'The user’s department.' },
    { value: 'user_title', label: 'Job title', description: 'The user’s job title / role.' },
    { value: 'user_org_unit', label: 'Org unit', description: 'The user’s organizational unit.' },
    { value: 'user_manager', label: 'Manager', description: 'The user’s manager.' },
    { value: 'user_id', label: 'Person', description: 'An individual user.' },
    { value: 'model', label: 'Model', description: 'The AI model used.' },
    { value: 'task_category', label: 'Task category', description: 'The classified category of work.' },
    { value: 'task_subcategory', label: 'Task subcategory', description: 'A finer-grained category of work.' },
    { value: 'difficulty', label: 'Difficulty', description: 'The classified difficulty of the task.' },
    { value: 'work_related', label: 'Work vs personal', description: 'Whether the session was work-related.' },
    { value: 'session_topic', label: 'Session topic', description: 'The detected topic of the session.' },
    { value: 'topic_label', label: 'Topic label', description: 'A human-readable topic label.' },
];

export const ASSIST_METRICS: AssistMetricDef[] = [
    { value: 'turns', label: 'Prompts', description: 'Count of prompt/response exchanges.' },
    { value: 'sessions', label: 'Sessions', description: 'Distinct conversation sessions.' },
    { value: 'users', label: 'Users', description: 'Distinct users.' },
    { value: 'totalTokens', label: 'Total tokens', description: 'Input + output tokens — a spend proxy.' },
    { value: 'inputTokens', label: 'Input tokens', description: 'Prompt tokens.' },
    { value: 'outputTokens', label: 'Output tokens', description: 'Response tokens.' },
    { value: 'avgOutputTokens', label: 'Avg output', description: 'Average output tokens per prompt.' },
    { value: 'avgConfidence', label: 'Avg confidence', description: 'Average classifier confidence.' },
];

export const ASSIST_VIZ_KINDS: AssistVizKind[] = ['ranked-bars', 'donut', 'trend'];

export const ASSIST_TIME_BUCKETS: TimeBucket[] = ['day', 'week', 'month'];

export const ASSIST_WORKFLOWS: AssistWorkflow[] = ['chart', 'filter', 'propose-field', 'unsupported'];

/**
 * Classification categories a proposed field can belong to. Keep in sync with
 * `ClassificationOptions.CATEGORY_OPTIONS` (the classification builder routes by
 * the lower-cased category).
 */
export const ASSIST_FIELD_CATEGORIES = ['Adoption', 'Usage', 'Governance', 'FinOps'] as const;

export type AssistFieldCategory = (typeof ASSIST_FIELD_CATEGORIES)[number];
